import { TableCollectionActionVisibility } from '../types/table-collection-action-visibility';
import { isNil, Nillable } from '@web/utils';
import { TableCollectionActionCommand } from '../types/table-collection-action-command';
import { TableCollectionActionDisability } from '../types/table-collection-action-disability';
import { TableCollectionAction } from '../models';
import { RequiredMethodCallException } from '@web/common';

export class TableCollectionActionBuilder<Item, Filter, Action> {
  private _key: Nillable<Action>;
  private _label: Nillable<string>;
  private _command: Nillable<TableCollectionActionCommand<Item, Filter>>;
  private _visible: Nillable<TableCollectionActionVisibility<Item, Filter>>;
  private _disability: Nillable<TableCollectionActionDisability<Item, Filter>>;

  public constructor() {
    this.reset();
  }

  public reset(): this {
    this._label = null;
    this._key = null;
    this._command = null;
    this._visible = null;
    this._disability = null;

    return this;
  }

  public init(key: Action): this {
    return this.key(key);
  }

  public key(key: Action): this {
    this._key = key;

    return this;
  }

  public command(command: TableCollectionActionCommand<Item, Filter>): this {
    this._command = command;

    return this;
  }

  public label(label: string): this {
    this._label = label;

    return this;
  }

  public visibility(
    visibility: TableCollectionActionVisibility<Item, Filter>,
  ): this {
    this._visible = visibility;

    return this;
  }

  public disability(
    disability: TableCollectionActionDisability<Item, Filter>,
  ): this {
    this._disability = disability;

    return this;
  }

  public build(): TableCollectionAction<Item, Filter, Action> {
    this.requireLabel();
    this.requireKey();
    this.requireCommand();

    this.configureDefaultVisibility();
    this.configureDefaultDisability();

    const action = new TableCollectionAction<Item, Filter, Action>(
      this._key!,
      this._label!,
      this._visible!,
      this._disability!,
      this._command!,
    );

    this.reset();

    return action;
  }

  private requireLabel(): void {
    if (!isNil(this._label)) {
      return;
    }

    throw new RequiredMethodCallException(this.label.name);
  }

  private requireKey(): void {
    if (!isNil(this._key)) {
      return;
    }

    throw new RequiredMethodCallException(this.key.name);
  }

  private requireCommand(): void {
    if (!isNil(this._command)) {
      return;
    }

    throw new RequiredMethodCallException(this.command.name);
  }

  private configureDefaultVisibility(): void {
    if (!isNil(this._visible)) {
      return;
    }

    this.visibility(() => true);
  }

  private configureDefaultDisability(): void {
    if (!isNil(this._disability)) {
      return;
    }

    this.disability((selection) => !selection);
  }
}
